import { z } from 'zod';
import { StudentSearch } from './fields.js';
/**
 * Admin Activity (ADR 0047): one student's timeline and the site-wide feed. Read-only. Rows come
 * newest first, ordered by (at, type, id); `cursor` is the opaque token from the previous page.
 */
export const ACTIVITY_TYPES = [
    'practice_answer',
    'todays_ten',
    'mock',
    'doubt',
    'flashcard_review',
    'purchase',
    'refund',
    'credit_change',
    'report',
    'league_entry',
];
export const ActivityTypeDto = z.enum(ACTIVITY_TYPES);
export const ACTIVITY_PAGE_SIZE = 25;
/** The widest window one request may ask for. */
export const ACTIVITY_MAX_RANGE_DAYS = 366;
const isoTime = z.iso.datetime({ offset: true });
/** Comma-separated types, e.g. `doubt,mock`. Empty or missing = every type. */
const typeList = z
    .string()
    .max(300)
    .transform((raw, ctx) => {
    const parts = raw
        .split(',')
        .map((part) => part.trim())
        .filter(Boolean);
    const bad = parts.filter((part) => !ACTIVITY_TYPES.includes(part));
    if (bad.length > 0) {
        ctx.addIssue({ code: 'custom', message: `unknown activity type: ${bad.join(', ')}` });
        return z.NEVER;
    }
    return [...new Set(parts)];
});
const activityQueryShape = {
    /** Window start (inclusive). Default: 30 days before `to` for a student, 7 for the feed. */
    from: isoTime.optional(),
    /** Window end (exclusive). Default: now. */
    to: isoTime.optional(),
    types: typeList.optional(),
    cursor: z.string().max(300).optional(),
    limit: z.coerce.number().int().min(1).max(100).optional(),
};
export const StudentActivityQuery = z.object(activityQueryShape).strict();
export const ActivityFeedQuery = z
    .object({
    ...activityQueryShape,
    /** Student name, @username or Telegram id. */
    q: StudentSearch.optional(),
})
    .strict();
const PracticeAnswerDetail = z.object({
    sessionId: z.uuid(),
    sessionKind: z.string(),
    itemId: z.uuid(),
    examKey: z.string(),
    topicPath: z.string(),
    choice: z.number().nullable(),
    correct: z.boolean().nullable(),
    unscored: z.boolean(),
    timeMs: z.number(),
});
const TodaysTenDetail = z.object({
    sessionId: z.uuid(),
    examKey: z.string(),
    questions: z.number(),
    answered: z.number(),
    correct: z.number(),
});
const MockDetail = z.object({
    attemptId: z.uuid(),
    mockId: z.uuid(),
    title: z.string(),
    status: z.string(),
    ranked: z.boolean(),
    rawScore: z.number().nullable(),
    maxScore: z.number().nullable(),
    correct: z.number().nullable(),
    wrong: z.number().nullable(),
    timeMs: z.number().nullable(),
});
const DoubtDetail = z.object({
    status: z.string(),
    question: z.string().nullable(),
    hasImage: z.boolean(),
    ocrText: z.string().nullable(),
    answer: z.string().nullable(),
    citations: z.array(z.object({
        marker: z.number(),
        title: z.string(),
        page: z.number().nullable(),
        url: z.string().nullable(),
    })),
    rating: z.number().nullable(),
    costPaise: z.number(),
    cached: z.boolean(),
    creditSource: z.string(),
    error: z.string().nullable(),
});
const FlashcardReviewDetail = z.object({
    cardId: z.uuid(),
    front: z.string(),
    rating: z.number(),
    wasNew: z.boolean(),
    intervalDays: z.number(),
});
const PurchaseDetail = z.object({
    planKey: z.string(),
    examKey: z.string().nullable(),
    rail: z.string(),
    status: z.string(),
    amount: z.number(),
    currency: z.string(),
    paidAt: z.string().nullable(),
});
const RefundDetail = z.object({
    orderId: z.uuid(),
    planKey: z.string(),
    amount: z.number(),
    currency: z.string(),
    status: z.string(),
    reason: z.string(),
});
const CreditChangeDetail = z.object({
    entitlementId: z.uuid(),
    entitlementType: z.string(),
    delta: z.number(),
    reason: z.string(),
});
const ReportDetail = z.object({
    itemId: z.uuid(),
    reason: z.string(),
    note: z.string().nullable(),
    status: z.string(),
});
const LeagueEntryDetail = z.object({
    grandMockId: z.uuid(),
    title: z.string(),
    eligibleVia: z.string().nullable(),
    agreedAt: z.string().nullable(),
    attemptId: z.uuid().nullable(),
});
/** Who a site-wide feed row belongs to (absent on a student's own timeline). */
export const ActivityStudentDto = z.object({
    userId: z.uuid(),
    name: z.string().nullable(),
    username: z.string().nullable(),
    telegramUserId: z.string(),
});
const base = {
    id: z.uuid(),
    /** ISO time in UTC, microseconds kept. */
    at: z.string(),
    userId: z.uuid(),
    student: ActivityStudentDto.optional(),
};
export const ActivityEntryDto = z.discriminatedUnion('type', [
    z.object({ ...base, type: z.literal('practice_answer'), detail: PracticeAnswerDetail }),
    z.object({ ...base, type: z.literal('todays_ten'), detail: TodaysTenDetail }),
    z.object({ ...base, type: z.literal('mock'), detail: MockDetail }),
    z.object({ ...base, type: z.literal('doubt'), detail: DoubtDetail }),
    z.object({ ...base, type: z.literal('flashcard_review'), detail: FlashcardReviewDetail }),
    z.object({ ...base, type: z.literal('purchase'), detail: PurchaseDetail }),
    z.object({ ...base, type: z.literal('refund'), detail: RefundDetail }),
    z.object({ ...base, type: z.literal('credit_change'), detail: CreditChangeDetail }),
    z.object({ ...base, type: z.literal('report'), detail: ReportDetail }),
    z.object({ ...base, type: z.literal('league_entry'), detail: LeagueEntryDetail }),
]);
export const ActivityPageDto = z.object({
    entries: z.array(ActivityEntryDto),
    /** Pass back as `cursor` for the next page; null on the last page. */
    nextCursor: z.string().nullable(),
    from: z.string(),
    to: z.string(),
});
//# sourceMappingURL=activity.js.map